import { create } from 'zustand';
import type {
  CustomerTagDto,
  CustomerStage,
  CustomerStageCountDto,
  CustomerDetailDto,
  CustomerSummaryDto,
  CustomerExportDto,
  GuestCustomerDto,
  UpdateCustomerRequest,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { saveFile } from '@/lib/download';

/**
 * The customer directory.
 *
 * Staff correct a name or a number; the owner blocks an account. Order counts
 * and lifetime spend are computed by the API and exclude cancelled orders, so
 * the number here matches what accounting sees. People who bought without an
 * account are listed beside them, by the email on their orders.
 */

export interface CustomerFilters {
  search: string;
  /** `YYYY-MM-DD`, when the account was created. */
  from: string;
  to: string;
  /** '' = any, 'yes' = has bought, 'no' = never has. */
  hasOrders: '' | 'yes' | 'no';
  /** '' = any, 'yes' = may sign in and buy, 'no' = switched off. */
  isActive: '' | 'yes' | 'no';
  /** Any of these tags (ADR-064). */
  tags: string[];
  /** Members of this customer group (ADR-085), and its name for the chip. */
  groupId: string;
  groupLabel: string;
  /** In this stage now (ADR-103); '' = any. */
  stage: CustomerStage | '';
}

export const EMPTY_CUSTOMER_FILTERS: CustomerFilters = {
  search: '',
  from: '',
  to: '',
  hasOrders: '',
  isActive: '',
  tags: [],
  groupId: '',
  groupLabel: '',
  stage: '',
};

interface CustomersState {
  items: CustomerSummaryDto[];
  nextCursor: string | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: CustomerFilters;
  total: number | null;

  /** The customer currently open, with their orders, returns, reviews and notes. */
  current: CustomerDetailDto | null;
  currentLoading: boolean;
  currentError: string | null;
  savingNote: boolean;
  /** An export or an erasure in progress (R10). */
  privacyBusy: boolean;
  /** An edit or a block in progress. */
  updating: boolean;

  /** Guest buyers — searched server-side, paged like the accounts. */
  guests: GuestCustomerDto[];
  guestsCursor: string | null;
  guestsTotal: number | null;
  guestsLoading: boolean;
  guestsSearch: string;

  /** The tags in use, with how many customers carry each (ADR-064). */
  tagsInUse: CustomerTagDto[];
  /** How many customers stand in each stage now (ADR-103); empty until loaded. */
  stageCounts: CustomerStageCountDto[];
  exporting: boolean;

  load: (filters?: Partial<CustomerFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  open: (id: string) => Promise<void>;
  addNote: (id: string, body: string) => Promise<boolean>;
  /** Everything held about a customer, for a request made to the store (R10, owner only). */
  exportData: (id: string) => Promise<CustomerExportDto | null>;
  /** Erases a customer on their request (R10, owner only). True when it is done. */
  erase: (id: string) => Promise<boolean>;
  /** A name or number corrected, or the account blocked (owner). */
  update: (id: string, patch: UpdateCustomerRequest) => Promise<boolean>;
  loadGuests: (search?: string) => Promise<void>;
  loadMoreGuests: () => Promise<void>;
  loadTags: () => Promise<void>;
  loadStageCounts: () => Promise<void>;
  /** The current filter as a spreadsheet, built on the server (ADR-064). */
  exportCsv: () => Promise<void>;
}

const PAGE_SIZE = 20;

export const useCustomersStore = create<CustomersState>((set, get) => ({
  items: [],
  nextCursor: null,
  loading: false,
  loadingMore: false,
  error: null,
  filters: EMPTY_CUSTOMER_FILTERS,
  total: null,
  current: null,
  currentLoading: false,
  currentError: null,
  savingNote: false,
  privacyBusy: false,
  updating: false,
  guests: [],
  guestsCursor: null,
  guestsTotal: null,
  guestsLoading: false,
  guestsSearch: '',
  tagsInUse: [],
  stageCounts: [],
  exporting: false,

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.customers.list(toQuery(next, PAGE_SIZE)));
      set({
        items: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load customers.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, items } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.customers.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );
      set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more customers.') });
    }
  },

  async open(id) {
    // Cleared first: showing the previous customer's orders under a new name,
    // even for a moment, is how support quotes the wrong history.
    set({ current: null, currentLoading: true, currentError: null });

    try {
      set({ current: await withSession(() => adminApi.customers.get(id)), currentLoading: false });
    } catch (error) {
      set({ currentLoading: false, currentError: errorMessage(error, 'Could not load this customer.') });
    }
  },

  async addNote(id, body) {
    set({ savingNote: true, currentError: null });

    try {
      const note = await withSession(() => adminApi.customers.addNote(id, body));

      set((state) => ({
        savingNote: false,
        current: state.current ? { ...state.current, notes: [note, ...state.current.notes] } : null,
      }));
      return true;
    } catch (error) {
      set({ savingNote: false, currentError: errorMessage(error, 'Could not save the note.') });
      return false;
    }
  },

  async exportData(id) {
    set({ privacyBusy: true, currentError: null });
    try {
      const data = await withSession(() => adminApi.customers.exportData(id));
      set({ privacyBusy: false });
      return data;
    } catch (error) {
      set({ privacyBusy: false, currentError: errorMessage(error, 'Could not export this customer.') });
      return null;
    }
  },

  async update(id, patch) {
    set({ updating: true, currentError: null });
    try {
      const updated = await withSession(() => adminApi.customers.update(id, patch));
      set((state) => ({
        updating: false,
        current: updated,
        items: state.items.map((row) =>
          row.id === id ? { ...row, name: updated.name, phone: updated.phone, isActive: updated.isActive } : row,
        ),
      }));
      return true;
    } catch (error) {
      set({ updating: false, currentError: errorMessage(error, 'Could not update this customer.') });
      return false;
    }
  },

  async loadTags() {
    try {
      const page = await withSession(() => adminApi.customers.tags());
      set({ tagsInUse: page.items });
    } catch {
      // The filter reads fine without suggestions.
      set({ tagsInUse: [] });
    }
  },

  async loadStageCounts() {
    try {
      const page = await withSession(() => adminApi.customers.stages());
      set({ stageCounts: page.items });
    } catch {
      // The list stands without its counts.
      set({ stageCounts: [] });
    }
  },

  async exportCsv() {
    const { filters, exporting } = get();
    if (exporting) return;
    set({ exporting: true, error: null });
    try {
      // The whole filter, not a page of it: the export takes no limit.
      const query = { ...toQuery(filters, 0), limit: undefined };
      await saveFile(() => withSession(() => adminApi.customers.export(query)), 'customers.csv');
    } catch (error) {
      set({ error: errorMessage(error, 'Could not export customers.') });
    } finally {
      set({ exporting: false });
    }
  },

  async loadGuests(search) {
    const term = search ?? get().guestsSearch;
    set({ guestsLoading: true, error: null, guestsSearch: term });
    try {
      const page = await withSession(() => adminApi.customers.guests({ limit: PAGE_SIZE, search: term || undefined }));
      set({ guests: page.items, guestsCursor: page.meta.nextCursor, guestsTotal: page.meta.total ?? null, guestsLoading: false });
    } catch (error) {
      set({ guestsLoading: false, error: errorMessage(error, 'Could not load guest buyers.') });
    }
  },

  async loadMoreGuests() {
    const { guestsCursor, guestsLoading, guestsSearch, guests } = get();
    if (!guestsCursor || guestsLoading) return;
    set({ guestsLoading: true });
    try {
      const page = await withSession(() =>
        adminApi.customers.guests({ limit: PAGE_SIZE, search: guestsSearch || undefined, cursor: guestsCursor }),
      );
      set({ guests: [...guests, ...page.items], guestsCursor: page.meta.nextCursor, guestsLoading: false });
    } catch (error) {
      set({ guestsLoading: false, error: errorMessage(error, 'Could not load more guest buyers.') });
    }
  },

  async erase(id) {
    set({ privacyBusy: true, currentError: null });
    try {
      await withSession(() => adminApi.customers.erase(id));
      // Gone from the directory as well as from the open page.
      set((state) => ({ privacyBusy: false, current: null, items: state.items.filter((row) => row.id !== id) }));
      return true;
    } catch (error) {
      set({ privacyBusy: false, currentError: errorMessage(error, 'Could not erase this customer.') });
      return false;
    }
  },
}));

function toQuery(filters: CustomerFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    // Absent, not false: "any" and "never bought" are different questions.
    hasOrders: filters.hasOrders === '' ? undefined : filters.hasOrders === 'yes',
    isActive: filters.isActive === '' ? undefined : filters.isActive === 'yes',
    tags: filters.tags.length > 0 ? filters.tags : undefined,
    groupId: filters.groupId || undefined,
    stage: filters.stage || undefined,
  };
}
